import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Form, ListGroup, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';

import { useOrganizationApi } from '@/shared/services/organizationApi';
import { highlightText } from '@/shared/utils/highlightText';
import './SearchAutocomplete.css';

const DEBOUNCE_MS = 300;
const MIN_CHARS = 2;
const MAX_SUGGESTIONS = 8;

export default function SearchAutocomplete({
    value,
    onChange,
    onSelect,
    placeholder = 'Search organizations...',
    size = 'lg',
    navigateOnSelect = false,
}) {
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [loading, setLoading] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);

    const inputRef = useRef(null);
    const suggestionsRef = useRef(null);
    const debounceRef = useRef(null);

    const orgApi = useOrganizationApi();
    const navigate = useNavigate();

    // Fetch suggestions with debounce
    const fetchSuggestions = useCallback(
        async (query) => {
            if (!query || query.length < MIN_CHARS) {
                setSuggestions([]);
                setShowSuggestions(false);
                return;
            }

            setLoading(true);
            try {
                const result = await orgApi.searchOrganizations(query, {
                    limit: MAX_SUGGESTIONS,
                });
                const orgs = result.organizations || [];
                setSuggestions(orgs);
                setShowSuggestions(orgs.length > 0);
                setActiveIndex(-1);
            } catch (err) {
                console.error('Search suggestions error:', err);
                setSuggestions([]);
            } finally {
                setLoading(false);
            }
        },
        [orgApi]
    );

    // Handle input change with debounce
    const handleInputChange = (e) => {
        const newValue = e.target.value;
        onChange(newValue);

        // Clear previous debounce
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }

        // Set new debounce
        debounceRef.current = setTimeout(() => {
            fetchSuggestions(newValue);
        }, DEBOUNCE_MS);
    };

    // Handle suggestion selection
    const handleSelect = (org) => {
        setShowSuggestions(false);
        setSuggestions([]);

        if (navigateOnSelect) {
            navigate(`/organizations/${org._id}`);
        } else if (onSelect) {
            onSelect(org);
        } else {
            onChange(org.name);
        }
    };

    // Handle keyboard navigation
    const handleKeyDown = (e) => {
        if (!showSuggestions || suggestions.length === 0) {
            return;
        }

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setActiveIndex((prev) =>
                    prev < suggestions.length - 1 ? prev + 1 : prev
                );
                break;
            case 'ArrowUp':
                e.preventDefault();
                setActiveIndex((prev) => (prev > 0 ? prev - 1 : -1));
                break;
            case 'Enter':
                e.preventDefault();
                if (activeIndex >= 0 && activeIndex < suggestions.length) {
                    handleSelect(suggestions[activeIndex]);
                }
                break;
            case 'Escape':
                setShowSuggestions(false);
                setActiveIndex(-1);
                break;
            default:
                break;
        }
    };

    // Close suggestions when clicking outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (
                inputRef.current &&
                !inputRef.current.contains(e.target) &&
                suggestionsRef.current &&
                !suggestionsRef.current.contains(e.target)
            ) {
                setShowSuggestions(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () =>
            document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Scroll active item into view
    useEffect(() => {
        if (activeIndex >= 0 && suggestionsRef.current) {
            const activeItem = suggestionsRef.current.children[activeIndex];
            if (activeItem) {
                activeItem.scrollIntoView({ block: 'nearest' });
            }
        }
    }, [activeIndex]);

    // Cleanup debounce on unmount
    useEffect(() => {
        return () => {
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }
        };
    }, []);

    return (
        <div className="search-autocomplete position-relative">
            <div className="position-relative">
                <Form.Control
                    ref={inputRef}
                    type="text"
                    size={size}
                    placeholder={placeholder}
                    value={value}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    onFocus={() => {
                        if (suggestions.length > 0) {
                            setShowSuggestions(true);
                        }
                    }}
                    autoComplete="off"
                    aria-autocomplete="list"
                    aria-expanded={showSuggestions}
                    aria-controls="search-suggestions"
                />
                {loading && (
                    <div className="search-autocomplete-spinner">
                        <Spinner
                            animation="border"
                            size="sm"
                            variant="primary"
                        />
                    </div>
                )}
            </div>

            {showSuggestions && suggestions.length > 0 && (
                <ListGroup
                    ref={suggestionsRef}
                    id="search-suggestions"
                    className="search-autocomplete-suggestions position-absolute w-100 shadow-sm"
                    role="listbox"
                >
                    {suggestions.map((org, index) => (
                        <ListGroup.Item
                            key={org._id}
                            action
                            active={index === activeIndex}
                            onClick={() => handleSelect(org)}
                            onMouseEnter={() => setActiveIndex(index)}
                            role="option"
                            aria-selected={index === activeIndex}
                        >
                            <div className="d-flex justify-content-between align-items-start">
                                <div>
                                    <strong className="d-block">
                                        {highlightText(org.name, value)}
                                    </strong>
                                    <small className="text-muted">
                                        {org.primarySector}
                                    </small>
                                </div>
                                {org.regionsServed?.length > 0 && (
                                    <small className="text-muted">
                                        {org.regionsServed[0]}
                                    </small>
                                )}
                            </div>
                        </ListGroup.Item>
                    ))}
                </ListGroup>
            )}
        </div>
    );
}

SearchAutocomplete.propTypes = {
    value: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
    onSelect: PropTypes.func,
    placeholder: PropTypes.string,
    size: PropTypes.string,
    navigateOnSelect: PropTypes.bool,
};
